/**
 * The services half of Teenused ja paketid: the list, and what opening one shows.
 *
 * Services used to be a grid of cards. A card grid says "browse these"; a service
 * is not something you browse, it is something you click through to - there are
 * four or five of them, they have a status and a count, and the question the
 * screen answers is "which one do I open". So they are pill rows now, the same
 * shape as clients, invoices and tasks, and the detail lives behind the row
 * rather than beside it.
 *
 * Fetching, deleting and the translation of the API's refusal codes all come from
 * the shared `useProducts` hook, so this file only decides what things look like.
 */
import { useState } from "react";
import { Boxes, Loader2, Plus } from "lucide-react";
import {
  ServiceIcon,
  localeText,
  makeT,
  serviceBadgeKey,
  serviceKind,
  useProducts,
  type Locale,
  type ProductDTO,
} from "@wa/ui";
import { useI18n, useT } from "../../../../i18n/I18nProvider";
import { api } from "../../../../lib/api";
import { Btn, CardHead, EmptyState, StatusPill, listCard } from "../../../ui";
import { ConfirmModal } from "../../_shared/ConfirmModal";
import { ProductFormModal } from "./ProductFormModal";
import { ServiceDetail } from "./ServiceDetail";
import { ServiceRowMenu } from "./ServiceRowMenu";

/** Service, what it is for, platform or catalogue, how many packages, menu. */
const COLS = "minmax(0,1.9fr) minmax(0,2.2fr) 1.1fr .7fr 44px";

/** `null` = the dialog is closed. `{ product: null }` = create. */
type EditTarget = { product: ProductDTO | null } | null;

export function ServicesPanel() {
  const { locale } = useI18n();
  const loc: Locale = locale === "en" ? "en" : "et";
  const ct = useT();
  const t = makeT(loc);

  const [selected, setSelected] = useState<string | null>(null);
  const [editing, setEditing] = useState<EditTarget>(null);

  const s = useProducts({
    api,
    locale: loc,
    onDeleted: (product) => {
      if (selected === product.code) setSelected(null);
    },
  });

  // Read out of the list rather than held in state, so an edit that renames the
  // service updates the head the moment the list reloads.
  const current = selected ? s.products.find((p) => p.code === selected) ?? null : null;

  return (
    <>
      {current ? (
        <ServiceDetail
          product={current}
          locale={loc}
          onBack={() => {
            setSelected(null);
            // Package counts may have moved while the detail was open.
            void s.reload();
          }}
          onEdit={() => setEditing({ product: current })}
          onDelete={() => s.askDelete(current)}
        />
      ) : (
        <div className="crm-card" style={listCard(COLS)}>
          <CardHead
            title={t("services.title")}
            actions={
              <Btn tone="brand" onClick={() => setEditing({ product: null })}>
                <Plus className="size-4" />
                {t("services.new")}
              </Btn>
            }
          />

          {s.loading ? (
            <div className="flex justify-center py-16 text-muted-foreground">
              <Loader2 className="size-6 animate-spin" />
            </div>
          ) : s.products.length === 0 ? (
            <EmptyState
              icon={Boxes}
              title={t("products.empty")}
              body={t("services.subtitle")}
              action={
                <Btn tone="brand" onClick={() => setEditing({ product: null })}>
                  <Plus className="size-4" />
                  {t("services.new")}
                </Btn>
              }
            />
          ) : (
            <>
              <div className="crm-list-head" style={{ gridTemplateColumns: COLS, paddingTop: 18 }}>
                <span>{t("editor.name")}</span>
                <span>{t("editor.desc")}</span>
                <span>{ct("plans.col.status")}</span>
                <span>{t("packages.title")}</span>
                <span />
              </div>

              <div className="crm-rows pt-0">
                {s.products.map((p) => (
                  <ServiceRow
                    key={p.code}
                    product={p}
                    locale={loc}
                    onOpen={() => setSelected(p.code)}
                    onEdit={() => setEditing({ product: p })}
                    onDelete={() => s.askDelete(p)}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {editing ? (
        <ProductFormModal
          // A fresh form per service: the hook seeds its fields once, at mount.
          key={editing.product?.code ?? "new"}
          product={editing.product}
          locale={loc}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void s.reload();
          }}
        />
      ) : null}

      {s.deleteTarget ? (
        // The body carries the hook's translated refusal once the server has
        // refused - "this service still has packages", "the platform service
        // cannot be deleted" - and the dialog stays open on it, so the sentence
        // lands where the operator is still looking.
        <ConfirmModal
          title={t("products.deleteConfirm")}
          body={s.deleteErr ?? localeText(s.deleteTarget.name, loc)}
          confirmLabel={t("delete")}
          cancelLabel={t("cancel")}
          onConfirm={s.doDelete}
          onClose={() => s.setDeleteTarget(null)}
        />
      ) : null}
    </>
  );
}

function ServiceRow({
  product,
  locale,
  onOpen,
  onEdit,
  onDelete,
}: {
  product: ProductDTO;
  locale: Locale;
  onOpen: () => void;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const t = makeT(locale);
  const description = product.description ? localeText(product.description, locale) : "";

  return (
    // A div, not a link: the row ends in a menu button, and interactive content
    // inside an <a> is invalid HTML and swallows the button's clicks. The name
    // control stretches an ::after over the whole row instead, so the row opens
    // end to end and the menu still sits on top of it.
    <div className="crm-list-row relative" style={{ gridTemplateColumns: COLS }}>
      <span className="flex min-w-0 items-center gap-3">
        <span className="crm-av crm-av-soft" aria-hidden>
          <ServiceIcon name={product.icon} className="size-[17px]" />
        </span>
        <span className="min-w-0">
          <button
            type="button"
            onClick={onOpen}
            className="crm-prow-n crm-trunc block max-w-full text-left after:absolute after:inset-0 after:rounded-full"
          >
            {localeText(product.name, locale)}
          </button>
          <span className="crm-prow-s crm-trunc block">{product.code}</span>
        </span>
      </span>

      <span className="crm-mut crm-trunc text-[12.5px]">{description || t("none")}</span>

      <span>
        {/* The platform service gates real features, the Veebihaldus one drives
            site monitoring, the rest are price lists. That is the one fact about a
            service worth carrying in the list. */}
        <StatusPill tone={serviceKind(product) === "display" ? "mut" : "info"}>
          {t(serviceBadgeKey(serviceKind(product)))}
        </StatusPill>
      </span>

      <span className="crm-tnum text-[13px]">{product.planCount ?? 0}</span>

      <ServiceRowMenu
        product={product}
        locale={locale}
        onOpen={onOpen}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </div>
  );
}
